import React, { useMemo } from 'react';
import { getOfficeConfig } from '../config/offices';

const TYPE_LABELS = {
  conference: 'Conference Rooms',
  meeting: 'Meeting Rooms',
  huddle: 'Huddle Rooms',
  office: 'Offices',
  cubicle: 'Cubicles',
};

const TYPE_ORDER = ['conference', 'meeting', 'huddle', 'office', 'cubicle'];

/**
 * RoomSchedule - Kiosk top-half component.
 * Shows all bookable rooms grouped by type with current status,
 * current meeting details, and rest-of-day schedule.
 */
export default function RoomSchedule({ availability = {}, onRoomSelect, section }) {
  const config = getOfficeConfig();
  const rooms = config.rooms;

  // Group bookable rooms by type (exclude assigned/non-bookable rooms)
  const roomsByType = useMemo(() => {
    const groups = {};
    rooms.filter(r => r.bookable !== false).forEach((room) => {
      if (!groups[room.type]) groups[room.type] = [];
      groups[room.type].push(room);
    });
    return groups;
  }, [rooms]);

  const now = new Date();
  const currentTimeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

  // For cubicles, show summary instead of individual cards
  const cubicleCount = useMemo(() => {
    const cubicles = rooms.filter((r) => r.type === 'cubicle' && r.bookable !== false);
    const available = cubicles.filter((r) => !availability[r.id]?.currentlyBooked);
    return { total: cubicles.length, available: available.length };
  }, [rooms, availability]);

  const officeCount = useMemo(() => {
    const offices = rooms.filter((r) => r.type === 'office' && r.bookable !== false);
    const available = offices.filter((r) => !availability[r.id]?.currentlyBooked);
    return { total: offices.length, available: available.length };
  }, [rooms, availability]);

  const ROOM_COLUMN_MAX = 8;
  const meetingTypes = ['conference', 'meeting', 'huddle'].filter(t => roomsByType[t]?.length > 0);

  // Section modes:
  // "calendar": conference/meeting/huddle columns only
  // "offices": offices summary only
  // "cubicles": cubicles summary only
  // "top" (legacy): calendar + offices
  // "bottom" (legacy): cubicles only
  // No section: render everything
  const showRooms = !section || section === 'calendar' || section === 'top';
  const showOffices = !section || section === 'offices' || section === 'top';
  const showCubicles = !section || section === 'cubicles' || section === 'bottom';

  return (
    <>
      {/* Conference / Meeting / Huddle as side-by-side columns */}
      {showRooms && meetingTypes.length > 0 && (
        <div className="kiosk-section kiosk-section-rooms">
          <div className="room-columns">
            {meetingTypes.map((type) => (
              <div key={type} className="room-column">
                <h3 className={`room-type-heading type-${type}`}>
                  {TYPE_LABELS[type]}
                </h3>
                <div className="room-card-list">
                  {roomsByType[type].slice(0, ROOM_COLUMN_MAX).map((room) => (
                    <RoomCard
                      key={room.id}
                      room={room}
                      availability={availability[room.id]}
                      currentTime={currentTimeStr}
                      onSelect={() => onRoomSelect?.(room.id)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Offices - full width */}
      {showOffices && roomsByType.office?.length > 0 && (
        <div className="kiosk-section kiosk-section-offices">
          <div className="room-type-group">
            <h3 className="room-type-heading type-office">Offices</h3>
            <CubicleOfficeSummary
              type="office"
              count={officeCount}
              rooms={roomsByType.office}
              availability={availability}
              onRoomSelect={onRoomSelect}
            />
          </div>
        </div>
      )}

      {/* Cubicles - full width */}
      {showCubicles && roomsByType.cubicle?.length > 0 && (
        <div className="kiosk-section kiosk-section-cubicles">
          <div className="room-type-group">
            <h3 className="room-type-heading type-cubicle">Cubicles</h3>
            <CubicleOfficeSummary
              type="cubicle"
              count={cubicleCount}
              rooms={roomsByType.cubicle}
              availability={availability}
              onRoomSelect={onRoomSelect}
            />
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Individual room card showing status, current meeting, and upcoming schedule.
 */
function RoomCard({ room, availability, currentTime, onSelect }) {
  const isBooked = availability?.currentlyBooked;
  const slots = availability?.slots || [];
  // Shared rooms (conference/meeting/huddle) are always clickable,
  // even when currently in use, because they support multiple time slots
  const isSharedRoom = room.type === 'conference' || room.type === 'meeting' || room.type === 'huddle';

  // Find the current active slot
  const currentSlot = slots.find(
    (s) => s.start <= currentTime && s.end > currentTime
  );

  // Get remaining time for the current meeting
  const timeRemaining = useMemo(() => {
    if (!currentSlot) return null;
    const [endH, endM] = currentSlot.end.split(':').map(Number);
    const now = new Date();
    const endDate = new Date();
    endDate.setHours(endH, endM, 0);
    const diffMin = Math.round((endDate - now) / 60000);
    if (diffMin <= 0) return null;
    if (diffMin < 60) return `${diffMin}m remaining`;
    const h = Math.floor(diffMin / 60);
    const m = diffMin % 60;
    return m > 0 ? `${h}h ${m}m remaining` : `${h}h remaining`;
  }, [currentSlot]);

  // Upcoming slots (after current time)
  const upcomingSlots = slots.filter((s) => s.start > currentTime).slice(0, 2);

  return (
    <div
      className={`room-card ${isBooked ? 'room-card--booked' : 'room-card--available'} ${isSharedRoom ? 'room-card--shared' : ''}`}
      onClick={() => (isSharedRoom || !isBooked) ? onSelect?.() : null}
    >
      <div className="room-card-top">
        <div className="room-card-name">{room.name}</div>
        <div className={`room-card-status ${isBooked ? 'status-booked' : 'status-available'}`}>
          {isBooked ? 'In Use' : 'Available'}
        </div>
      </div>

      {currentSlot && (
        <div className="room-card-current">
          <div className="current-meeting-title">
            {currentSlot.organizer || currentSlot.bookedBy || 'Reserved'}
          </div>
          <div className="current-meeting-info">
            {timeRemaining && <span className="current-meeting-remaining">{timeRemaining}</span>}
          </div>
        </div>
      )}

      {upcomingSlots.length > 0 && (
        <div className="room-card-schedule">
          {upcomingSlots.map((slot, i) => (
            <div key={i} className="schedule-slot">
              <span className="slot-time">{formatTime(slot.start)} - {formatTime(slot.end)}</span>
              <span className="slot-title">{slot.organizer || 'Reserved'}</span>
            </div>
          ))}
        </div>
      )}

      {!currentSlot && upcomingSlots.length === 0 && (
        <div className="room-card-empty">Open all day</div>
      )}
    </div>
  );
}

/**
 * Summary card for cubicles/offices showing count and list of available ones.
 */
function CubicleOfficeSummary({ type, count, rooms, availability, onRoomSelect }) {
  const availableRooms = rooms.filter((r) => !availability[r.id]?.currentlyBooked);
  const bookedRooms = rooms.filter((r) => availability[r.id]?.currentlyBooked);

  return (
    <div className="summary-card">
      <div className="summary-card-header">
        <div className={`summary-count ${count.available > 0 ? 'has-available' : 'none-available'}`}>
          <span className="summary-number">{count.available}</span>
          <span className="summary-label">of {count.total} available</span>
        </div>
        {count.available > 0 && (
          <button
            className="summary-book-btn"
            onClick={() => onRoomSelect?.(type === 'cubicle' ? 'any-cubicle' : 'any-office')}
          >
            Book Any {type === 'cubicle' ? 'Cubicle' : 'Office'}
          </button>
        )}
      </div>

      <div className="summary-room-list">
        {availableRooms.map((room) => (
          <span
            key={room.id}
            className="summary-room-tag available-tag"
            onClick={() => onRoomSelect?.(room.id)}
          >
            {room.id}
          </span>
        ))}
        {bookedRooms.map((room) => (
          <span key={room.id} className="summary-room-tag booked-tag">
            {room.id}
          </span>
        ))}
      </div>

    </div>
  );
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  // Handle both "HH:MM" and ISO datetime "2026-05-15T14:00:00" formats
  let h, m;
  if (timeStr.includes('T')) {
    const d = new Date(timeStr.endsWith('Z') ? timeStr : timeStr + 'Z');
    // Convert UTC to Central time (offset -5 or -6 depending on DST)
    const central = new Date(d.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
    h = central.getHours();
    m = central.getMinutes();
  } else {
    [h, m] = timeStr.split(':').map(Number);
  }
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${hour}:${m.toString().padStart(2, '0')} ${suffix}`;
}
